import React, { useState } from "react";
import { del, get, patch, post } from "../api";
import { errText, getLang, setLang, t } from "../i18n";
import { getState, navigate, refreshMe, setState, toast, useStore } from "../store";
import { AVATAR_CHOICES, Avatar, Button, Field, Link, Modal, cx, useAsync } from "../ui";
import { disconnect } from "../api";

export function Settings() {
  const me = useStore((s) => s.me)!;
  const [displayName, setName] = useState(me.displayName);
  const [bio, setBio] = useState(me.bio ?? "");
  const [avatar, setAvatar] = useState<string | null>(me.avatarUrl);
  const [privacy, setPrivacy] = useState({ showOnline: true, allowFriendRequests: true, allowRoomInvites: "friends", allowDirect: "friends", dmNotifications: true, showStats: true, ...me.privacy });
  const [pw, setPw] = useState({ current: "", next: "" });
  const [deleting, setDeleting] = useState(false);
  const blocked = useAsync(() => get("/api/blocks"));
  const save = async (body: object) => {
    try {
      await patch("/api/me", body);
      await refreshMe();
      toast(t.saved);
    } catch (e) {
      toast(errText(e), "error");
    }
  };
  const signOut = async (all = false) => {
    await post(all ? "/api/auth/logout-all" : "/api/auth/logout").catch(() => {});
    disconnect();
    setState({ me: null, balance: null });
    navigate("/", true);
  };
  return (
    <div className="settings">
      <h1>{t.settings}</h1>
      <section className="panel">
        <h2>{t.profileSettings}</h2>
        <form className="stack" onSubmit={(e) => { e.preventDefault(); void save({ displayName, bio, avatarUrl: avatar }); }}>
          <Field label={t.avatar}>
            <div className="avatar-pick">
              <button type="button" className={cx(!avatar && "is-on")} onClick={() => setAvatar(null)}><Avatar name={displayName} size={48} /></button>
              {AVATAR_CHOICES.map((a) => (
                <button type="button" key={a} className={cx(avatar === `avatar:${a}` && "is-on")} onClick={() => setAvatar(`avatar:${a}`)}>
                  <Avatar name={displayName} url={`avatar:${a}`} size={48} />
                </button>
              ))}
            </div>
          </Field>
          <Field label={t.displayName}><input value={displayName} onChange={(e) => setName(e.target.value)} maxLength={40} required /></Field>
          <Field label={t.bio}><textarea value={bio} onChange={(e) => setBio(e.target.value)} maxLength={160} rows={2} /></Field>
          <Button type="submit">{t.save}</Button>
        </form>
      </section>
      <section className="panel">
        <h2>{t.language}</h2>
        <div className="segmented">
          {(["ar", "en"] as const).map((l) => (
            <button key={l} className={getLang() === l ? "is-on" : ""} onClick={() => { setLang(l); void save({ locale: l }); setState({}); }}>
              {l === "ar" ? "العربية" : "English"}
            </button>
          ))}
        </div>
      </section>
      <section className="panel">
        <h2>{t.privacySettings}</h2>
        <div className="stack">
          {(["showOnline", "allowFriendRequests", "dmNotifications", "showStats"] as const).map((k) => (
            <label key={k} className="check">
              <input type="checkbox" checked={!!privacy[k]} onChange={(e) => { const p = { ...privacy, [k]: e.target.checked }; setPrivacy(p); void save({ privacy: p }); }} />
              <span>{t[k]}</span>
            </label>
          ))}
          <Field label={t.allowInvites}>
            <select value={privacy.allowRoomInvites} onChange={(e) => { const p = { ...privacy, allowRoomInvites: e.target.value }; setPrivacy(p); void save({ privacy: p }); }}>
              <option value="everyone">{t.everyone}</option>
              <option value="friends">{t.friendsOnly}</option>
              <option value="nobody">{t.nobody}</option>
            </select>
          </Field>
          <Field label={t.allowDirect} hint={t.allowDirectHint}>
            <select value={privacy.allowDirect} onChange={(e) => { const p = { ...privacy, allowDirect: e.target.value }; setPrivacy(p); void save({ privacy: p }); }}>
              <option value="everyone">{t.everyone}</option>
              <option value="friends">{t.friendsOnly}</option>
              <option value="nobody">{t.nobody}</option>
            </select>
          </Field>
          {blocked.data?.blocked?.length > 0 && (
            <div>
              <h3>{t.blockedUsers}</h3>
              <ul className="list">
                {blocked.data.blocked.map((b: any) => (
                  <li key={b.id} className="list-row">
                    <span className="grow">{b.display_name}</span>
                    <Button kind="quiet" onClick={async () => { await del(`/api/blocks/${b.id}`); blocked.reload(); }}>{t.unblock}</Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
      <section className="panel">
        <h2>{t.security}</h2>
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await post("/api/me/password", pw);
              setPw({ current: "", next: "" });
              toast(t.saved);
            } catch (e) {
              toast(errText(e), "error");
            }
          }}
        >
          <Field label={t.currentPassword}><input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" dir="ltr" required /></Field>
          <Field label={t.newPassword} hint={t.passwordHint}><input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} minLength={8} autoComplete="new-password" dir="ltr" required /></Field>
          <Button type="submit" kind="ghost">{t.changePassword}</Button>
        </form>
        <div className="row wrap">
          <Button kind="ghost" onClick={() => signOut(false)}>{t.signOut}</Button>
          <Button kind="ghost" onClick={() => signOut(true)}>{t.signOutEverywhere}</Button>
          <Button kind="danger" onClick={() => setDeleting(true)}>{t.deleteAccount}</Button>
        </div>
      </section>
      <nav className="row wrap muted">
        <Link to="/help">{t.help}</Link>
        <Link to="/privacy">{t.privacy}</Link>
        <Link to="/terms">{t.terms}</Link>
      </nav>
      {deleting && <DeleteAccount onClose={() => setDeleting(false)} />}
    </div>
  );
}

function DeleteAccount({ onClose }: { onClose: () => void }) {
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={t.deleteAccount} onClose={onClose}>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await del("/api/me", { password, confirm });
            disconnect();
            setState({ me: null, balance: null });
            navigate("/", true);
          } catch (e) {
            toast(errText(e), "error");
            setBusy(false);
          }
        }}
      >
        <p>{t.deleteWarn}</p>
        <Field label={t.typeDelete}><input value={confirm} onChange={(e) => setConfirm(e.target.value)} dir="ltr" /></Field>
        <Field label={t.password}><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" /></Field>
        <Button kind="danger" type="submit" busy={busy} disabled={confirm !== "DELETE" || !password}>{t.deleteAccount}</Button>
      </form>
    </Modal>
  );
}

void getState;
